import { Scenario, Scene, ScenarioMetadata } from '../../types';
import { defaultChapterEndItem } from '../../systems/chapter-end-content';

/** Corps d'un bloc scène v2 sans son id (fourni par l'appelant à l'insertion). */
export type SceneBody = Scene extends infer S ? (S extends Scene ? Omit<S, 'id'> : never) : never;

export function isReleasedToProd(metadata: ScenarioMetadata): boolean {
  if (metadata.releaseToProd === true) return true;
  if (metadata.releaseToProd === false) return false;
  return metadata.hiddenFromSelection !== true;
}

export function metadataWithReleaseToProd(metadata: ScenarioMetadata, releaseToProd: boolean): ScenarioMetadata {
  const next: ScenarioMetadata = { ...metadata, releaseToProd };
  if (releaseToProd) {
    delete next.hiddenFromSelection;
  } else {
    next.hiddenFromSelection = true;
  }
  return next;
}

export function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

export function updateScenario(scenario: Scenario, patch: Partial<Scenario>): Scenario {
  return { ...scenario, ...patch };
}

export function updateScene(scenario: Scenario, sceneId: string, patch: Partial<Scene>): Scenario {
  return {
    ...scenario,
    scenes: scenario.scenes.map((s) => (s.id === sceneId ? ({ ...s, ...patch } as Scene) : s)),
  };
}

export function addScene(scenario: Scenario, scene: Scene): Scenario {
  return { ...scenario, scenes: [...scenario.scenes, scene] };
}

export function removeScene(scenario: Scenario, sceneId: string): Scenario {
  return { ...scenario, scenes: scenario.scenes.filter((s) => s.id !== sceneId) };
}

export function moveScene(scenario: Scenario, sceneId: string, direction: -1 | 1): Scenario {
  const scenes = [...scenario.scenes];
  const idx = scenes.findIndex((s) => s.id === sceneId);
  if (idx < 0) return scenario;
  const newIdx = idx + direction;
  if (newIdx < 0 || newIdx >= scenes.length) return scenario;
  const [item] = scenes.splice(idx, 1);
  scenes.splice(newIdx, 0, item!);
  return { ...scenario, scenes };
}

export const CONTENT_TYPE_LABELS: Record<string, string> = {
  dialogue: 'Dialogue',
  choice: 'Choix',
  command: 'Commande',
  textOverlay: 'Overlay',
  chapterEnd: 'Fin de chapitre',
  confidenceGain: 'Gain confiance',
  phoneChat: 'SMS',
  phoneCall: 'Appel',
  phoneMail: 'Mail téléphone',
  phoneMailInteract: 'Mail interactif',
  passwordGame: 'Jeu mots de passe',
  dragDropGame: 'Jeu glisser-déposer',
  situationSortGame: 'Jeu de tri',
  timedOverlay: 'Overlay temporisé',
  observation: 'Observation',
  clickableScreen: 'Écran cliquable',
  gameOver: 'Game over',
  recordCard: "Fiche d'arrestation",
};

export function defaultLetterOverlayItem(): Extract<SceneBody, { type: 'textOverlay' }> {
  return {
    type: 'textOverlay',
    overlayType: 'letter',
    title: '',
    content: '',
    enableTyping: true,
    continueText: 'Commencer la mission',
  };
}

export function defaultContentItem(type: string): SceneBody {
  switch (type) {
    case 'dialogue':
      return { type: 'dialogue', speaker: '', text: '' };
    case 'choice':
      return { type: 'choice', prompt: '', options: [{ text: 'Option 1' }] };
    case 'command':
      return { type: 'command', command: 'setFlag', target: '' };
    case 'textOverlay':
      return defaultLetterOverlayItem();
    case 'chapterEnd':
      return defaultChapterEndItem();
    case 'confidenceGain':
      return { type: 'confidenceGain', characters: [] };
    case 'phoneChat':
      return {
        type: 'phoneChat',
        contactName: '',
        messages: [{ sender: 'contact', text: '' }],
      };
    case 'phoneCall':
      return {
        type: 'phoneCall',
        contactName: '',
        messages: [{ sender: 'contact', text: '' }],
        style: 'inline_call',
        incomingCall: true,
      };
    case 'phoneMail':
      return { type: 'phoneMail', from: '', subject: '', content: '' };
    case 'phoneMailInteract':
      return { type: 'phoneMailInteract', from: '', subject: '' };
    case 'passwordGame':
      return {
        type: 'passwordGame',
        rounds: [],
        victoryReward: '',
        screenPosition: { top: 5, left: 17, width: 66, height: 58 },
      };
    case 'dragDropGame':
      return {
        type: 'dragDropGame',
        phrases: [],
        requiredSelections: 3,
        resultRules: [],
        victoryReward: '',
        starsTotal: 3,
      };
    case 'situationSortGame':
      return {
        type: 'situationSortGame',
        categories: [{ id: 'cat1', label: 'Catégorie 1' }],
        situations: [],
        requireAllCorrect: true,
      };
    case 'timedOverlay':
      return { type: 'timedOverlay', message: '', durationMs: 2000 };
    case 'observation':
      return {
        type: 'observation',
        instruction: "Qu'est-ce qui cloche ? Cliquez sur les éléments suspects.",
        elements: [],
        exitChoice: { text: 'Continuer' },
      };
    case 'clickableScreen':
      return {
        type: 'clickableScreen',
        instruction: '',
        zones: [],
      };
    case 'gameOver':
      return { type: 'gameOver', heading: 'GAME OVER', text: '' };
    case 'recordCard':
      return {
        type: 'recordCard',
        recordKind: 'arrestation',
        title: '',
        taken: [],
        saved: [],
        continueText: 'Continuer →',
      };
    default:
      return { type: 'dialogue', text: '' };
  }
}

export function getContentItemLabel(item: Scene | SceneBody): string {
  if (item.type === 'textOverlay' && item.overlayType === 'chapterEnd') {
    return 'Fin de chapitre (legacy)';
  }
  return CONTENT_TYPE_LABELS[item.type] ?? item.type;
}

function truncate(text: string, max = 60): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

/** Short human-readable preview of a content block, shown next to its label in lists. */
export function getContentItemSummary(item: Scene | SceneBody): string {
  switch (item.type) {
    case 'dialogue':
      return truncate([item.speaker, item.text].filter(Boolean).join(' : ') || '(vide)');
    case 'choice':
      return truncate(item.prompt || `${item.options?.length ?? 0} option(s)`);
    case 'command':
      return truncate(`${item.command} → ${item.target || '…'}`);
    case 'textOverlay':
      return truncate(
        item.characterName || item.title || item.content || item.overlayType || 'letter'
      );
    case 'chapterEnd':
      return truncate(item.heading || item.eyebrow || 'Fin de chapitre');
    case 'confidenceGain':
      return truncate(`${item.characters?.length ?? 0} personnage(s)`);
    case 'phoneChat':
    case 'phoneCall':
      return truncate(item.contactName || '(contact ?)');
    case 'phoneMail':
    case 'phoneMailInteract':
      return truncate(item.subject || item.from || '(mail)');
    case 'passwordGame':
      return truncate(`${item.rounds?.length ?? 0} manche(s)`);
    case 'dragDropGame':
      return truncate(`${item.phrases?.length ?? 0} phrase(s)`);
    case 'situationSortGame':
      return truncate(`${item.situations?.length ?? 0} situation(s)`);
    case 'timedOverlay':
      return truncate(item.message || `${item.durationMs} ms`);
    case 'observation': {
      const objects = item.elements?.length ?? 0;
      const anomalies = item.elements?.filter((e) => (e.pedaShields ?? 0) > 0).length ?? 0;
      const hint = item.instruction ? ` — ${item.instruction}` : '';
      return truncate(`${objects} objet(s), ${anomalies} anomalie(s)${hint}`);
    }
    case 'clickableScreen': {
      const zoneCount = item.zones?.length ?? 0;
      const hint = item.instruction ? ` — ${item.instruction}` : '';
      return truncate(`${zoneCount} zone(s)${hint}`);
    }
    case 'gameOver':
      return truncate(item.text || item.heading || 'Game over');
    case 'recordCard':
      return truncate(item.title || item.summary || "Fiche d'arrestation");
    default:
      return '';
  }
}

/** Compact glyph used as a visual badge for each content type in lists. */
export function getContentItemIcon(item: Scene | SceneBody): string {
  switch (item.type) {
    case 'dialogue':
      return '💬';
    case 'choice':
      return '🔀';
    case 'command':
      return '⚙️';
    case 'textOverlay':
      return '📄';
    case 'chapterEnd':
      return '🏁';
    case 'confidenceGain':
      return '💗';
    case 'phoneChat':
      return '📱';
    case 'phoneCall':
      return '📞';
    case 'phoneMail':
    case 'phoneMailInteract':
      return '✉️';
    case 'passwordGame':
      return '🔑';
    case 'dragDropGame':
      return '🧩';
    case 'situationSortGame':
      return '🗂️';
    case 'timedOverlay':
      return '⏱️';
    case 'observation':
      return '🔍';
    case 'clickableScreen':
      return '🖱️';
    case 'gameOver':
      return '💀';
    case 'recordCard':
      return '🪪';
    default:
      return '•';
  }
}

/** One-line summary of a scene block for the scene list (type + elements). */
export function getSceneSummary(scene: Scene): string {
  const parts: string[] = [getContentItemLabel(scene)];
  const summary = getContentItemSummary(scene);
  if (summary) parts.push(summary);
  if (scene.elements?.length) parts.push(`${scene.elements.length} élément(s)`);
  return parts.join(' · ');
}

let uniqueIdSeq = 0;

export function uniqueId(prefix: string): string {
  uniqueIdSeq += 1;
  return `${prefix}_${Date.now().toString(36)}_${uniqueIdSeq.toString(36)}`;
}
